import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import CloseIcon from '@mui/icons-material/Close';
import { GetAdminFeedbackItemAPI, SetAdminFeedbackStatusAPI } from '../../../../api/AdminAPI';
import { FeedbackTypeBadge, NpsCategoryBadge, NPS_STATUS_OPTIONS } from './FeedbackBadges';
import FeedbackFileAttachment from './FeedbackFileAttachment';
import FeedbackReviewForm from './FeedbackReviewForm';
import { formatDate } from '../../../../utils/Utils';

/**
 * Formata uma data 'Y-m-d H:i:s' usando formatDate() do projeto.
 * @param {string|null} dateStr
 * @returns {string}
 */
function fmtDate(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr.replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return '—';
  return formatDate(date);
}

/**
 * FeedbackDetailsDialog - Diálogo de detalhes de um feedback ou avaliação NPS (Admin Geral)
 *
 * Busca o item completo (incluindo anexos) ao abrir. No NPS o status é salvo na hora;
 * no pedido de problema/melhoria a revisão (status, classificação, resposta) é salva
 * pelo FeedbackReviewForm, que também avisa o usuário quando o status pede.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {'feedback'|'nps'} props.type
 * @param {number|string|null} props.id
 * @param {Function} props.onClose
 * @param {Function} props.onStatusSaved - chamado após salvar status ou revisão: (response) => void
 */
function FeedbackDetailsDialog({ open, type, id, onClose, onStatusSaved }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [item, setItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [statusError, setStatusError] = useState('');

  useEffect(() => {
    if (!open || !id) return;
    setItem(null);
    setError(null);
    setStatusError('');
    setLoading(true);
    GetAdminFeedbackItemAPI(type, id, (response) => {
      if (!response || response.success === false) {
        setError(response?.error || 'Erro ao carregar os detalhes. Tente novamente.');
        setLoading(false);
        return;
      }
      setItem(response.item);
      setLoading(false);
    });
  }, [open, type, id]);

  const handleStatusChange = (e) => {
    const newStatus = e.target.value;
    if (!item) return;
    setSaving(true);
    setStatusError('');
    SetAdminFeedbackStatusAPI(type, id, newStatus, (response) => {
      setSaving(false);
      if (!response || response.success === false) {
        setStatusError(response?.error || 'Erro ao salvar o status. Tente novamente.');
        return;
      }
      setItem((prev) => ({ ...prev, ...response.item }));
      if (onStatusSaved) onStatusSaved(response);
    });
  };

  const handleReviewSaved = (response) => {
    setItem((prev) => ({ ...prev, ...response.item }));
    if (onStatusSaved) onStatusSaved(response);
  };

  const files = item?.files || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth={type === 'nps' ? 'sm' : 'md'} fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.125rem' }}>
          {type === 'nps' ? 'Detalhes da avaliação' : 'Detalhes do feedback'}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : item ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {type === 'nps' ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>{item.rating}/10</Typography>
                <NpsCategoryBadge category={item.category} />
              </Box>
            ) : (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <FeedbackTypeBadge type={item.type} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  {item.title}
                </Typography>
              </Box>
            )}

            <Typography variant="body2" sx={{ color: 'text.primary', whiteSpace: 'pre-wrap' }}>
              {type === 'nps' ? (item.comments || 'Sem comentários.') : item.description}
            </Typography>

            <Divider />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {[
                { label: 'Usuário', value: `${item.user_name || '—'}${item.user_email ? ` (${item.user_email})` : ''}` },
                {
                  label: 'Empresa',
                  value: item.company_id ? (
                    <Link component={RouterLink} to={`/admin/companies/${item.company_id}`}>
                      {item.company_name || `Empresa #${item.company_id}`}
                    </Link>
                  ) : (item.company_name || '—'),
                },
                { label: 'Tela', value: item.screen || '—' },
                { label: 'Criado em', value: fmtDate(item.created_at) },
                {
                  label: 'Atualizado em',
                  value: `${fmtDate(item.updated_at)}${item.updated_by_name ? ` · ${item.updated_by_name}` : ''}`,
                },
              ].map((row) => (
                <Box key={row.label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Typography variant="body2" color="text.secondary">{row.label}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>{row.value}</Typography>
                </Box>
              ))}
            </Box>

            {files.length > 0 && (
              <>
                <Divider />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Anexos ({files.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {files.map((file) => (
                      <FeedbackFileAttachment key={file.id} file={file} />
                    ))}
                  </Box>
                </Box>
              </>
            )}

            <Divider />

            {type === 'feedback' ? (
              <FeedbackReviewForm item={item} onSaved={handleReviewSaved} />
            ) : (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>Status</Typography>
                <Select
                  size="small"
                  value={item.status}
                  onChange={handleStatusChange}
                  disabled={saving}
                  sx={{ minWidth: 200, borderRadius: '8px' }}
                >
                  {NPS_STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
                {statusError && (
                  <Alert severity="error" sx={{ mt: 1 }}>{statusError}</Alert>
                )}
              </Box>
            )}
          </Box>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export default FeedbackDetailsDialog;
